// frontend/src/pages/EventCreatePage.jsx

import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "api";
import BackButton from "../components/BackButton";
import LocationAutoComplete from "../components/LocationAutoComplete";
import DateTimeField12h from "../components/DateTimeField12h";

export default function EventCreatePage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [title, setTitle] = useState("");
  const [startTime, setStartTime] = useState("");
  const [locationText, setLocationText] = useState("");
  const [description, setDescription] = useState("");
  const [locationUrl, setLocationUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (startTime) return;

    const pad = (n) => String(n).padStart(2, "0");
    const step = 5;
    const now = new Date();
    const mins = now.getMinutes();
    const next = new Date(now);

    next.setMinutes(mins + (step - (mins % step || step)));
    next.setSeconds(0);
    next.setMilliseconds(0);

    const y = next.getFullYear();
    const m = pad(next.getMonth() + 1);
    const d = pad(next.getDate());
    const h = pad(next.getHours());
    const mi = pad(next.getMinutes());

    setStartTime(`${y}-${m}-${d}T${h}:${mi}`);
  }, [startTime]);

  const canSave = title.trim() && startTime;

  const goBackToCircle = () => {
    navigate(`/circles/${id}`, { replace: true });
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!canSave || saving) return;

    setSaving(true);
    setErr("");

    try {
      const payload = {
        title: title.trim(),
        start_time: new Date(startTime).toISOString(),
        description: description.trim() || null,
        location_text: locationText.trim() || null,
        location_url: locationUrl.trim() || null,
      };

      await api.post(`/groups/${Number(id)}/events`, payload);

      navigate(`/circles/${id}?created=1`, { replace: true });
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Failed to create event");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-blue-50">
      <header
        className="sticky top-0 left-0 right-0 z-30 bg-blue-50"
        style={{ paddingTop: "env(safe-area-inset-top)" }}
      >
        <div className="mt-2 px-4 flex items-center">
          <BackButton className="text-blue-600" ariaLabel="Back" />
          <h1 className="text-[20px] text-blue-600 dm-font font-black tracking-[-0.06em] leading-[0.9] select-none">
            Create Event
          </h1>
        </div>
      </header>

      <main
        className="px-5 pb-6"
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 24px)" }}
      >
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1 ml-3">
              Event Name
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full border rounded-2xl !min-h-0 !min-w-0 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
              placeholder="Cooking Class @Sur La Table"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1 ml-3">
              Date & Time
            </label>
            <div className="w-full">
              <DateTimeField12h
                value={startTime}
                onChange={setStartTime}
                minuteStep={5}
                className=""
                wideDate
              />
            </div>
          </div>

          <div>
            <label className="block text-sm ml-3 font-medium mb-1">
              Location
            </label>
            <div className="relative z-[1000] overflow-visible">
              <LocationAutoComplete
                apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
                value={locationText}
                onChange={(val) => setLocationText(val)}
                inputClassName="py-1.5"
                placeholder=""
                country="us"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm ml-3 font-medium mb-1">
              Description
            </label>
            <textarea
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full border rounded-2xl p-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
            />
          </div>

          <div>
            <label className="block text-sm font-medium ml-3 mb-1">
              Online Link
            </label>
            <input
              type="url"
              value={locationUrl}
              onChange={(e) => setLocationUrl(e.target.value)}
              placeholder="Zoom/Meet link or any URL"
              className="w-full border rounded-2xl p-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
            />
          </div>

          {err && <p className="text-sm text-red-600">{err}</p>}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={goBackToCircle}
              className="bg-white text-blue-600 text-sm rounded-full font-medium px-3 !min-h-0 !h-8"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={!canSave || saving}
              className="bg-blue-600 text-white text-sm font-medium rounded-full px-3 !min-h-0 !h-8 disabled:opacity-50"
            >
              {saving ? "Saving…" : "Create"}
            </button>
          </div>
        </form>
      </main>
    </div>
  );
}